// MTXbudget: the walk through the coming pays. Each pay must cover the charges
// due before the next one; what is left is spread over the whole horizon by a
// linear program solved level by level (no late charge, then credit use under
// 30 % of the limit, then least interest, then dated needs). Pure: the solver
// is handed in. All amounts are integer cents, dates are 'YYYY-MM-DD'.

import {
  addDays,
  grossPay,
  hoursForNet,
  netPay,
  nextPayday,
  withheldTax,
} from './mtxbudget';
import type {
  Debt,
  Occurrence,
  OptimiseOption,
  PayConfig,
  PayInput,
  Project,
} from './mtxbudget';
import { withArrangements } from './mtxbudget-completer';

/** Money other than the salary, usable from `date` onwards. */
export type ExtraIncome = {
  id: string;
  name: string;
  amount: number;
  date: string;
};

export type WalkDebt = Debt & {
  /** Credit limit of a card or line, in cents. */
  creditLimit?: number;
  /** Schedules whose payments reduce this debt. */
  paymentScheduleIds?: string[];
  repaymentScheduleIds?: string[];
  /** The account of a card: a charge paid from it adds to what is owed. */
  accountId?: string;
  /** What the bank says is still available on the card, in cents. */
  bankRoom?: number;
};

export type WalkInputs = {
  config: PayConfig;
  today: string;
  /** The day the balances stand at: the first pay is the one on or after it. */
  from?: string;
  /** Scheduled occurrences over the horizon; income is ignored. */
  occurrences: Occurrence[];
  pays?: Record<string, PayInput>;
  /** Cash on hand before the first payday. */
  available?: number;
  /** Cash kept in the account after every pay. */
  cushion?: number;
  incomes?: ExtraIncome[];
  /** Occurrence ids whose payment was switched off by hand. */
  skipped?: Record<string, unknown>;
  debts?: WalkDebt[];
  /** Dated investment needs, served after the debts. */
  needs?: Project[];
  count?: number;
};

export type WalkCharge = Occurrence & {
  skipped: boolean;
  /** The debt this payment reduces. */
  debtId?: string;
  /** The card it is charged to: it leaves no cash, it adds to that debt. */
  cardId?: string;
  /** The card it was meant for, which has no room left for it. */
  refusedBy?: string;
};

type Routed = Pick<
  WalkDebt,
  'id' | 'accountId' | 'paymentScheduleIds' | 'repaymentScheduleIds'
>;

/**
 * Where a charge goes: the debt it pays down, when it is one of that debt's
 * payments, or the card it is put on, when its account is a debt's account.
 * A charge put on a card leaves no cash: it adds to that debt.
 */
export function routeOf(
  charge: Occurrence,
  debts: readonly Routed[],
): { debtId: string | undefined; cardId: string | undefined } {
  const { scheduleId } = charge;
  const debtId = debts.find(
    d =>
      scheduleId !== undefined &&
      (d.paymentScheduleIds?.includes(scheduleId) ||
        d.repaymentScheduleIds?.includes(scheduleId)),
  )?.id;
  const cardId = debts.find(
    d =>
      d.accountId !== undefined &&
      d.accountId === charge.account &&
      d.id !== debtId,
  )?.id;
  return { debtId, cardId };
}

type Limited = Routed &
  Partial<Pick<WalkDebt, 'balance' | 'creditLimit' | 'bankRoom'>>;

/**
 * The room the bank reports on a card. The bridge sends a card's available
 * credit with either sign (7.79 $ left on a card at 1 992.21 $ of a 2 000 $
 * limit came as -7.79): its size is what counts.
 */
export function cardRoomFromBank(available: number): number {
  return Math.abs(available);
}

/**
 * Where each charge goes, in the order of the charges given. A charge meant
 * for a card goes on it only while the card has room for it: its limit, less
 * what is owed (or less, when the bank says so), plus what was paid to it
 * since. Beyond that, the card turns
 * it down and it leaves the account like any other charge. A card with no
 * known limit takes everything, as before.
 */
export function routeCharges<T extends Occurrence>(
  charges: readonly T[],
  debts: readonly Limited[],
  skipped: Record<string, unknown> = {},
): (T & {
  debtId: string | undefined;
  cardId: string | undefined;
  refusedBy?: string;
})[] {
  // The lesser of the book's room and the bank's: the bank also takes off
  // the purchases still pending.
  const room = new Map(
    debts.flatMap(debt =>
      debt.creditLimit === undefined
        ? []
        : [
            [
              debt.id,
              Math.min(
                debt.creditLimit - Math.max(0, debt.balance ?? 0),
                debt.bankRoom ?? Infinity,
              ),
            ],
          ],
    ),
  );
  const routed: (T & {
    debtId: string | undefined;
    cardId: string | undefined;
    refusedBy?: string;
  })[] = charges.map(charge => ({
    ...charge,
    ...routeOf(charge, debts),
  }));
  // Day after day; on a same day, what is paid to a card before what is
  // bought with it.
  const order = routed
    .map((charge, index) => ({ charge, index }))
    .sort(
      (a, b) =>
        a.charge.date.localeCompare(b.charge.date) ||
        Number(a.charge.debtId === undefined) -
          Number(b.charge.debtId === undefined) ||
        a.index - b.index,
    );
  for (const { charge } of order) {
    // A payment switched off by hand is neither paid nor bought.
    if (skipped[charge.id]) {
      continue;
    }
    const spent = -charge.amount;
    if (charge.debtId !== undefined && room.has(charge.debtId)) {
      room.set(charge.debtId, (room.get(charge.debtId) ?? 0) + spent);
    }
    if (charge.cardId !== undefined && room.has(charge.cardId)) {
      const left = room.get(charge.cardId) ?? 0;
      if (spent > left) {
        charge.refusedBy = charge.cardId;
        charge.cardId = undefined;
      } else {
        room.set(charge.cardId, left - spent);
      }
    }
  }
  return routed;
}

/** The charges the salary has to pay: those no card takes. */
export function cashCharges<T extends Occurrence>(
  charges: readonly T[],
  debts: readonly Limited[],
): T[] {
  const routed = routeCharges(charges, debts);
  return charges.filter((_, index) => routed[index].cardId === undefined);
}

/** What really leaves the account: not switched off, not put on a card. */
function isCash(charge: WalkCharge): boolean {
  return !charge.skipped && charge.cardId === undefined;
}

type Period = {
  index: number;
  payday: string;
  nextPayday: string;
  periodStart: string;
  periodEnd: string;
  status: 'received' | 'worked' | 'open' | 'future';
  hours: [number, number];
  gross: number;
  tax: number;
  deductions: number;
  net: number;
  netIsEstimate: boolean;
  charges: WalkCharge[];
  incomes: ExtraIncome[];
};

/** What the plan decides: extra payments per debt and per need, by pay. */
export type Decisions = {
  debt: Record<string, number[]>;
  need: Record<string, number[]>;
};

export type WalkStep = Period & {
  cashIn: number;
  /**
   * What the pay before left short, paid back on payday: a gap no pay filled
   * stays in the account until a pay does.
   */
  refund: number;
  totalCharges: number;
  totalIncomes: number;
  /** Cents missing for the charges of this pay; 0 when the pay is enough. */
  shortfall: number;
  /** Net this pay must bring, and the weekly hours that bring it. */
  requiredNet: number;
  thresholdHoursPerWeek: number;
  /** Cash left in the account once this pay is spread. */
  cashOut: number;
  /** Part of `cashOut` kept for a later pay that is not enough on its own. */
  setAside: number;
  setAsideFor: string | null;
  debts: {
    id: string;
    name: string;
    rate: number;
    limit: number | null;
    interest: number;
    scheduled: number;
    /** Part of a scheduled payment beyond what was still owed. */
    overpaid: number;
    /** Charges put on this card during the pay. */
    purchases: number;
    extra: number;
    balanceBefore: number;
    balanceAfter: number;
  }[];
  needs: {
    id: string;
    name: string;
    wished: string;
    amount: number;
    paid: number;
    paidSoFar: number;
  }[];
};

export type LpSolve = (lp: string) => {
  status: string;
  objective: number;
  values: Record<string, number>;
};

export type Check = { name: string; ok: boolean; detail: string };

const FORTNIGHTS_PER_YEAR = 26;
const TARGET_USE = 0.3;
/** How much an over-limit cent weighs against a cent above 30 %. */
const OVER_LIMIT_WEIGHT = 4;

function fortnightRate(debt: Debt): number {
  return debt.rate / 100 / FORTNIGHTS_PER_YEAR;
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** The pays of the horizon with what each one receives and has to pay. */
export function buildPeriods(inputs: WalkInputs): Period[] {
  const { config, today, pays = {}, count = 13 } = inputs;
  const debts = inputs.debts ?? [];
  const skipped = inputs.skipped ?? {};
  const first = nextPayday(config, inputs.from ?? today);
  const charges = routeCharges(
    inputs.occurrences
      .filter(o => o.amount < 0)
      .sort((a, b) => a.date.localeCompare(b.date)),
    debts,
    skipped,
  );
  const incomes = [...(inputs.incomes ?? [])].sort((a, b) =>
    a.date.localeCompare(b.date),
  );
  const minHours: [number, number] = [
    config.minHoursPerWeek,
    config.minHoursPerWeek,
  ];

  return Array.from({ length: count }, (_, index) => {
    const payday = addDays(first, 14 * index);
    const next = addDays(payday, 14);
    const periodEnd = addDays(payday, -config.payDelayDays);
    const periodStart = addDays(periodEnd, -13);
    // Anything dated before the very first payday belongs to the first pay.
    const inWindow = (date: string) =>
      date < next && (index === 0 || date >= payday);
    const input = pays[payday] ?? {};
    const hours = input.hours ?? minHours;
    const gross = grossPay(config, hours);
    const status: Period['status'] =
      input.received !== undefined
        ? 'received'
        : today > periodEnd
          ? 'worked'
          : today >= periodStart
            ? 'open'
            : 'future';
    return {
      index,
      payday,
      nextPayday: next,
      periodStart,
      periodEnd,
      status,
      hours,
      gross,
      tax: withheldTax(config, gross),
      deductions: config.fixedDeductions + config.cappedContributions,
      net: input.received ?? netPay(config, hours),
      netIsEstimate: input.received === undefined,
      charges: charges
        .filter(c => inWindow(c.date))
        .map(c => ({ ...c, skipped: Boolean(skipped[c.id]) })),
      incomes: incomes.filter(i => inWindow(i.date)),
    };
  });
}

function datedNeeds(inputs: WalkInputs, periods: Period[]): Project[] {
  const end = periods[periods.length - 1]?.nextPayday ?? '';
  return (inputs.needs ?? [])
    .filter(n => n.wished !== undefined && n.wished < end && n.amount > 0)
    .sort((a, b) => a.priority - b.priority);
}

/** The pays that can still fund a need: those received by its date. */
function fundingPays(need: Project, periods: Period[]): number[] {
  const eligible = periods
    .filter(p => p.payday <= (need.wished ?? ''))
    .map(p => p.index);
  return eligible.length > 0 ? eligible : [0];
}

type Flow = { date: string; amount: number };

/** Money in and out of one pay, in the order it happens. */
function flows(period: Period & { refund?: number }, spent: number): Flow[] {
  const list: Flow[] = [
    { date: period.payday, amount: period.net },
    { date: period.payday, amount: -(period.refund ?? 0) },
    ...period.incomes.map(i => ({ date: i.date, amount: i.amount })),
    { date: period.payday, amount: -spent },
    ...period.charges
      .filter(isCash)
      .map(c => ({ date: c.date, amount: c.amount })),
  ];
  // On a same day, money comes in before it goes out.
  return list
    .filter(flow => flow.amount !== 0)
    .sort((a, b) => a.date.localeCompare(b.date) || b.amount - a.amount);
}

/**
 * Replays decisions with additions and subtractions only: every figure shown
 * comes from here, never from the solver's floating-point output. A pay left
 * short is lent what it misses, and the next pay pays it back (`carry`, the
 * prudent way); without `carry`, each pay is measured on its own charges, as
 * Optimiser does to weigh its options.
 */
export function replay(
  inputs: WalkInputs,
  decisions: Decisions,
  carry = true,
): WalkStep[] {
  const periods = buildPeriods(inputs);
  const cushion = inputs.cushion ?? 0;
  const needs = datedNeeds(inputs, periods);
  const balances = new Map(
    (inputs.debts ?? []).map(d => [d.id, Math.max(0, d.balance)]),
  );
  const paidToNeed = new Map<string, number>();
  let cash = inputs.available ?? 0;
  let owed = 0;

  const steps = periods.map((period): WalkStep => {
    const cashIn = cash;
    const refund = carry ? owed : 0;
    const debts = (inputs.debts ?? []).map(debt => {
      const start = balances.get(debt.id) ?? 0;
      const interest = Math.round(start * fortnightRate(debt));
      const due = sum(
        period.charges
          .filter(c => !c.skipped && c.debtId === debt.id)
          .map(c => -c.amount),
      );
      const scheduled = Math.min(start + interest, due);
      const purchases = sum(
        period.charges
          .filter(c => !c.skipped && c.cardId === debt.id)
          .map(c => -c.amount),
      );
      const balanceBefore = start + interest - scheduled + purchases;
      const extra = Math.min(
        balanceBefore,
        Math.max(0, decisions.debt[debt.id]?.[period.index] ?? 0),
      );
      balances.set(debt.id, balanceBefore - extra);
      return {
        id: debt.id,
        name: debt.name,
        rate: debt.rate,
        limit: debt.creditLimit ?? null,
        interest,
        scheduled,
        overpaid: due - scheduled,
        purchases,
        extra,
        balanceBefore,
        balanceAfter: balanceBefore - extra,
      };
    });
    const stepNeeds = needs.map(need => {
      const before = paidToNeed.get(need.id) ?? 0;
      const paid = fundingPays(need, periods).includes(period.index)
        ? Math.min(
            need.amount - before,
            Math.max(0, decisions.need[need.id]?.[period.index] ?? 0),
          )
        : 0;
      paidToNeed.set(need.id, before + paid);
      return {
        id: need.id,
        name: need.name,
        wished: need.wished ?? '',
        amount: need.amount,
        paid,
        paidSoFar: before + paid,
      };
    });
    const spent =
      sum(debts.map(d => d.extra)) + sum(stepNeeds.map(n => n.paid));

    let running = cashIn;
    let lowest = Infinity;
    for (const flow of flows({ ...period, refund }, spent)) {
      running += flow.amount;
      if (flow.amount < 0) {
        lowest = Math.min(lowest, running);
      }
    }
    const shortfall = Math.max(0, -lowest, cushion - running);
    const cashOut = running + shortfall;
    cash = cashOut;
    owed = shortfall;

    return {
      ...period,
      cashIn,
      refund,
      totalCharges: sum(period.charges.filter(isCash).map(c => -c.amount)),
      totalIncomes: sum(period.incomes.map(i => i.amount)),
      shortfall,
      requiredNet: 0,
      thresholdHoursPerWeek: 0,
      cashOut,
      setAside: 0,
      setAsideFor: null,
      debts,
      needs: stepNeeds,
    };
  });

  // What a pay must bring: enough, day after day, for its own charges, and
  // what the pays after it need to find in the account when they start.
  let target = cushion;
  for (let index = steps.length - 1; index >= 0; index--) {
    const step = steps[index];
    let running = step.cashIn;
    let need = 0;
    for (const flow of flows({ ...step, net: 0 }, 0)) {
      running += flow.amount;
      if (flow.date >= step.payday && flow.amount < 0) {
        need = Math.max(need, -running);
      }
    }
    // Never more than the plan really leaves in the account: a later pay
    // that no pay can rescue is shown as short, not as hours to work now.
    step.requiredNet = Math.max(
      0,
      need,
      Math.min(target, step.cashOut) - running,
    );
    step.thresholdHoursPerWeek = hoursForNet(inputs.config, step.requiredNet);
    // The least this pay must find in the account to hold on its own.
    let alone = 0;
    let lowest = 0;
    for (const flow of flows(step, 0)) {
      alone += flow.amount;
      if (flow.amount < 0) {
        lowest = Math.min(lowest, alone);
      }
    }
    target = Math.max(cushion, -lowest, target - alone);
  }

  // Cash kept above the cushion waits for the next pay that draws on it.
  steps.forEach((step, index) => {
    const kept = step.cashOut - cushion;
    const user = steps
      .slice(index + 1)
      .find(later => later.cashOut < later.cashIn);
    if (kept >= 100 && user) {
      step.setAside = kept;
      step.setAsideFor = user.payday;
    }
  });
  return steps;
}

// ── The linear program ─────────────────────────────────────────────────────

type Level = 'shortfall' | 'overpaid' | 'creditUse' | 'interest' | 'needs';
// `overpaid` comes second: no plan may hide an extra payment behind a
// scheduled one that is larger than what is still owed.
const LEVELS: Level[] = [
  'shortfall',
  'overpaid',
  'creditUse',
  'interest',
  'needs',
];

function term(coefficient: number, name: string): string {
  const value = Math.abs(coefficient).toFixed(8);
  return `${coefficient < 0 ? '-' : '+'} ${value} ${name}`;
}

/**
 * The plan as a linear program in CPLEX LP format. Variables, per pay `i`:
 * `c_i` cash at the end, `m_i` cents missing, `x_d_i` extra payment to debt
 * `d`, `b_d_i` its balance, `u_d_i` / `v_d_i` what stands above 30 % of the
 * limit / above the limit, `y_k_i` payment to need `k`, `s_k` what need `k`
 * still lacks at its date.
 */
export function buildLp(
  inputs: WalkInputs,
  level: Level,
  locks: { level: Level; value: number }[] = [],
): string | null {
  const periods = buildPeriods(inputs);
  const debts = inputs.debts ?? [];
  const needs = datedNeeds(inputs, periods);
  const cushion = inputs.cushion ?? 0;
  const available = inputs.available ?? 0;

  const objectives: Record<Level, string[]> = {
    shortfall: periods.map(p => term(1, `m_${p.index}`)),
    overpaid: [],
    creditUse: [],
    interest: [],
    needs: needs.map((_, k) => term(2 ** (needs.length - 1 - k), `s_${k}`)),
  };
  const rows: string[] = [];
  const bounds: string[] = [];

  for (const period of periods) {
    const i = period.index;
    const spending = [
      ...debts.flatMap((d, index) =>
        d.canPayMore === false ? [] : [`x_${index}_${i}`],
      ),
      ...needs.flatMap((need, k) =>
        fundingPays(need, periods).includes(i) ? [`y_${k}_${i}`] : [],
      ),
    ];
    const previous = i === 0 ? [] : [term(-1, `c_${i - 1}`)];
    // What the pay before was lent, paid back on payday.
    const payBack = i === 0 ? [] : [term(-1, `m_${i - 1}`)];
    const carried = i === 0 ? available : 0;

    // Day after day the account never goes below zero: one row at the end of
    // every day money goes out, payday included since the plan spends then.
    const all = flows(period, 0);
    const days = [
      ...new Set([
        ...all.filter(flow => flow.amount < 0).map(flow => flow.date),
        ...(spending.length > 0 || i > 0 ? [period.payday] : []),
      ]),
    ].sort();
    for (const day of days) {
      const upTo =
        carried + sum(all.filter(f => f.date <= day).map(f => f.amount));
      rows.push(
        ` day_${i}_${day.replaceAll('-', '')}: ${[
          ...previous.map(t => t.replace('-', '+')),
          term(1, `m_${i}`),
          ...(day >= period.payday
            ? [...payBack, ...spending.map(name => term(-1, name))]
            : []),
        ].join(' ')} >= ${(-upTo).toFixed(2)}`,
      );
    }
    const balance = carried + sum(all.map(flow => flow.amount));
    rows.push(
      ` cash_${i}: ${[
        term(1, `c_${i}`),
        ...previous,
        term(-1, `m_${i}`),
        ...payBack.map(t => t.replace('-', '+')),
        ...spending.map(name => term(1, name)),
      ].join(' ')} = ${balance.toFixed(2)}`,
    );
    bounds.push(` c_${i} >= ${cushion.toFixed(2)}`);

    debts.forEach((debt, index) => {
      const growth = 1 + fortnightRate(debt);
      const scheduled = sum(
        period.charges
          .filter(c => !c.skipped && c.debtId === debt.id)
          .map(c => -c.amount),
      );
      const purchases = sum(
        period.charges
          .filter(c => !c.skipped && c.cardId === debt.id)
          .map(c => -c.amount),
      );
      const extra =
        debt.canPayMore === false ? [] : [term(1, `x_${index}_${i}`)];
      // `o` absorbs a scheduled payment larger than what is still owed.
      rows.push(
        i === 0
          ? ` debt_${index}_0: ${[term(1, `b_${index}_0`), ...extra, term(-1, `o_${index}_0`)].join(' ')} = ${(Math.max(0, debt.balance) * growth - scheduled + purchases).toFixed(2)}`
          : ` debt_${index}_${i}: ${[term(1, `b_${index}_${i}`), term(-growth, `b_${index}_${i - 1}`), ...extra, term(-1, `o_${index}_${i}`)].join(' ')} = ${(purchases - scheduled).toFixed(2)}`,
      );
      objectives.interest.push(term(fortnightRate(debt), `b_${index}_${i}`));
      objectives.overpaid.push(term(1, `o_${index}_${i}`));
      if (debt.creditLimit) {
        rows.push(
          ` use_${index}_${i}: ${term(1, `u_${index}_${i}`)} ${term(-1, `b_${index}_${i}`)} >= ${(-TARGET_USE * debt.creditLimit).toFixed(2)}`,
          ` over_${index}_${i}: ${term(1, `v_${index}_${i}`)} ${term(-1, `b_${index}_${i}`)} >= ${(-debt.creditLimit).toFixed(2)}`,
        );
        objectives.creditUse.push(
          term(1, `u_${index}_${i}`),
          term(OVER_LIMIT_WEIGHT, `v_${index}_${i}`),
        );
      }
    });
  }

  needs.forEach((need, k) => {
    rows.push(
      ` need_${k}: ${[
        term(1, `s_${k}`),
        ...fundingPays(need, periods).map(i => term(1, `y_${k}_${i}`)),
      ].join(' ')} = ${need.amount.toFixed(2)}`,
    );
  });

  if (objectives[level].length === 0) {
    return null;
  }
  for (const lock of locks) {
    // A sliver of room, far below a cent, so that rounding never makes a
    // locked level infeasible and never lets a later level buy anything.
    rows.push(
      ` lock_${lock.level}: ${objectives[lock.level].join(' ')} <= ${(Math.max(0, lock.value) * (1 + 1e-9) + 0.001).toFixed(6)}`,
    );
  }
  return [
    'Minimize',
    ` obj: ${objectives[level].join(' ')}`,
    'Subject To',
    ...rows,
    'Bounds',
    ...bounds,
    'End',
  ].join('\n');
}

/** Solves the levels in order, each one locked before the next. */
export function optimise(inputs: WalkInputs, solve: LpSolve): Decisions {
  const periods = buildPeriods(inputs);
  const debts = inputs.debts ?? [];
  const needs = datedNeeds(inputs, periods);
  const locks: { level: Level; value: number }[] = [];
  let values: Record<string, number> = {};
  for (const level of LEVELS) {
    const lp = buildLp(inputs, level, locks);
    if (lp === null) {
      continue;
    }
    const solution = solve(lp);
    if (solution.status !== 'Optimal') {
      throw new Error(`mtxbudget: ${level} ${solution.status}`);
    }
    locks.push({ level, value: solution.objective });
    values = solution.values;
  }
  // Rounded down to the cent: the plan never spends money it does not have.
  // The sliver added first undoes the solver's own floating-point error.
  const cents = (name: string) =>
    Math.max(0, Math.floor((values[name] ?? 0) + 1e-4));
  return {
    debt: Object.fromEntries(
      debts.map((debt, index) => [
        debt.id,
        periods.map(p => cents(`x_${index}_${p.index}`)),
      ]),
    ),
    need: Object.fromEntries(
      needs.map((need, k) => [
        need.id,
        periods.map(p => cents(`y_${k}_${p.index}`)),
      ]),
    ),
  };
}

/**
 * The plain way, pay by pay, with no look ahead: what a pay leaves goes to
 * the cards above 30 % of their limit, then to the costliest debt, then to
 * the dated needs. Kept as a witness the optimisation must never do worse than.
 */
export function cascade(inputs: WalkInputs): Decisions {
  const periods = buildPeriods(inputs);
  const debts = inputs.debts ?? [];
  const needs = datedNeeds(inputs, periods);
  const cushion = inputs.cushion ?? 0;
  const decisions: Decisions = {
    debt: Object.fromEntries(debts.map(d => [d.id, periods.map(() => 0)])),
    need: Object.fromEntries(needs.map(n => [n.id, periods.map(() => 0)])),
  };
  for (const period of periods) {
    const step = replay(inputs, decisions)[period.index];
    let running = step.cashIn;
    let free = Infinity;
    for (const flow of flows(step, 0)) {
      running += flow.amount;
      if (flow.date >= period.payday) {
        free = Math.min(free, running);
      }
    }
    free = Math.max(0, Math.min(free, running - cushion));
    const above = (d: (typeof step.debts)[number]) =>
      d.limit === null
        ? 0
        : Math.max(0, d.balanceBefore - TARGET_USE * d.limit);
    const payable = step.debts.filter(
      d => debts.find(debt => debt.id === d.id)?.canPayMore !== false,
    );
    const given = new Map<string, number>();
    const give = (id: string, wanted: number) => {
      const amount = Math.min(free, Math.max(0, Math.floor(wanted)));
      given.set(id, (given.get(id) ?? 0) + amount);
      free -= amount;
    };
    [...payable]
      .sort(
        (a, b) =>
          Number(b.limit !== null && b.balanceBefore > b.limit) -
            Number(a.limit !== null && a.balanceBefore > a.limit) ||
          b.rate - a.rate,
      )
      .forEach(d => give(d.id, above(d)));
    [...payable]
      .sort((a, b) => b.rate - a.rate)
      .forEach(d => give(d.id, d.balanceBefore - (given.get(d.id) ?? 0)));
    for (const [id, amount] of given) {
      decisions.debt[id][period.index] = amount;
    }
    for (const need of step.needs) {
      if (
        fundingPays(
          needs.find(n => n.id === need.id) ?? needs[0],
          periods,
        ).includes(period.index)
      ) {
        const amount = Math.min(free, need.amount - need.paidSoFar);
        decisions.need[need.id][period.index] = amount;
        free -= amount;
      }
    }
  }
  return decisions;
}

/** What a plan costs on each level, to compare two plans. */
export function score(steps: WalkStep[]): Record<Level, number> {
  const last = steps[steps.length - 1];
  return {
    shortfall: sum(steps.map(s => s.shortfall)),
    overpaid: sum(steps.flatMap(s => s.debts.map(d => d.overpaid))),
    creditUse: sum(
      steps.flatMap(s =>
        s.debts.map(d =>
          d.limit === null
            ? 0
            : Math.max(0, d.balanceAfter - TARGET_USE * d.limit) +
              OVER_LIMIT_WEIGHT * Math.max(0, d.balanceAfter - d.limit),
        ),
      ),
    ),
    interest: sum(steps.flatMap(s => s.debts.map(d => d.interest))),
    needs: sum(
      (last?.needs ?? []).map(
        (n, k, all) => 2 ** (all.length - 1 - k) * (n.amount - n.paidSoFar),
      ),
    ),
  };
}

/** The walk: optimised decisions, replayed in exact cents. */
export function walkFortnights(inputs: WalkInputs, solve: LpSolve): WalkStep[] {
  return replay(inputs, optimise(inputs, solve));
}

// ── What an investment takes ───────────────────────────────────────────────

type Wanted = { name: string; amount: number };

/**
 * The plan with an investment paid before the debts, as a charge due on
 * `date`, every pay still to be worked and received by then being worked at
 * least `hoursPerWeek`.
 */
function withInvestment(
  inputs: WalkInputs,
  wanted: Wanted,
  date: string,
  hoursPerWeek: number,
): WalkInputs {
  const pays = { ...inputs.pays };
  for (const period of buildPeriods(inputs)) {
    const workable = period.status === 'open' || period.status === 'future';
    if (workable && period.payday <= date) {
      pays[period.payday] = {
        ...pays[period.payday],
        hours: [
          Math.max(period.hours[0], hoursPerWeek),
          Math.max(period.hours[1], hoursPerWeek),
        ],
      };
    }
  }
  return {
    ...inputs,
    pays,
    occurrences: [
      ...inputs.occurrences,
      {
        id: `investment/${date}`,
        name: wanted.name,
        date,
        amount: -wanted.amount,
        recurring: false,
      },
    ],
  };
}

function totalShortfall(inputs: WalkInputs, solve: LpSolve): number {
  // Each pay on its own: a gap carried along must not count twice.
  return score(replay(inputs, optimise(inputs, solve), false)).shortfall;
}

/**
 * The weekly hours, on the pays still to be worked before `date`, that pay
 * an investment on that date when it goes before the debts. `hours` is null
 * when even `maxHours` is not enough, `missing` then saying by how much.
 */
export function hoursToFund(
  inputs: WalkInputs,
  wanted: Wanted,
  date: string,
  maxHours: number,
  solve: LpSolve,
): { hours: number | null; missing: number } {
  // What is already missing without it is not its doing.
  const base = totalShortfall(inputs, solve);
  const short = (hours: number) =>
    Math.max(
      0,
      totalShortfall(withInvestment(inputs, wanted, date, hours), solve) - base,
    );
  const floor = inputs.config.minHoursPerWeek;
  if (short(floor) < 100) {
    return { hours: floor, missing: 0 };
  }
  const atMost = short(maxHours);
  if (atMost >= 100) {
    return { hours: null, missing: atMost };
  }
  // By half hours, between the guaranteed hours and the most one can work.
  let low = Math.round(floor * 2);
  let high = Math.round(maxHours * 2);
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (short(middle / 2) < 100) {
      high = middle;
    } else {
      low = middle;
    }
  }
  return { hours: high / 2, missing: 0 };
}

/**
 * The earliest day an investment can be paid before the debts when every pay
 * is worked at `maxHours`: a payday, or `from` when it can be paid by then.
 * Null when the walk never gets there.
 */
export function earliestFundingDay(
  inputs: WalkInputs,
  wanted: Wanted,
  maxHours: number,
  solve: LpSolve,
  from = inputs.today,
): string | null {
  const base = totalShortfall(inputs, solve);
  for (const period of buildPeriods(inputs)) {
    // Before the very first pay, the cash on hand may already be enough.
    const days =
      period.index === 0 && from < period.payday
        ? [from, period.payday]
        : [period.payday < from ? from : period.payday];
    for (const day of days) {
      if (day >= period.nextPayday) {
        continue;
      }
      const plan = withInvestment(inputs, wanted, day, maxHours);
      if (totalShortfall(plan, solve) - base < 100) {
        return day;
      }
    }
  }
  return null;
}

// ── What closes a shortfall in time ────────────────────────────────────────

const NO_DECISIONS: Decisions = { debt: {}, need: {} };
/** A shortfall under a dollar is rounding, not a charge left unpaid. */
const COVERED = 100;

/**
 * Cents the pays miss, each one for its own charges and on their dates,
 * before any extra payment: no plan leaves less short. Money that comes
 * after a charge never pays it. The cushion is a comfort, not a charge: it
 * is left out.
 */
export function leastShortfall(inputs: WalkInputs): number {
  return score(replay({ ...inputs, cushion: 0 }, NO_DECISIONS, false))
    .shortfall;
}

/** What each pay misses for its own charges, by payday, cushion aside. */
export function shortfallByPay(inputs: WalkInputs): Record<string, number> {
  return Object.fromEntries(
    replay({ ...inputs, cushion: 0 }, NO_DECISIONS, false).map(step => [
      step.payday,
      step.shortfall,
    ]),
  );
}

/** A charge the pays leave unpaid on its day, in whole or in part. */
export type ShortCharge = {
  payday: string;
  status: Period['status'];
  id: string;
  date: string;
  name: string;
  /** Cents of the charge, and the part of it no money reaches. */
  amount: number;
  missing: number;
  refusedBy?: string;
};

/**
 * The charges behind each pay's shortfall, cushion aside: going day by day,
 * each charge that takes the account deeper than it went before misses that
 * much. They add up to `leastShortfall`.
 */
export function shortCharges(inputs: WalkInputs): ShortCharge[] {
  return replay({ ...inputs, cushion: 0 }, NO_DECISIONS, false).flatMap(
    step => {
      if (step.shortfall < COVERED) {
        return [];
      }
      const moves = [
        { date: step.payday, amount: step.net, charge: null },
        ...step.incomes.map(i => ({
          date: i.date,
          amount: i.amount,
          charge: null,
        })),
        ...step.charges
          .filter(isCash)
          .map(c => ({ date: c.date, amount: c.amount, charge: c })),
      ]
        .filter(move => move.amount !== 0)
        .sort((a, b) => a.date.localeCompare(b.date) || b.amount - a.amount);
      let running = step.cashIn;
      let deepest = 0;
      return moves.flatMap(move => {
        running += move.amount;
        const deeper = Math.max(0, -running - deepest);
        deepest += deeper;
        return move.charge && deeper > 0
          ? [
              {
                payday: step.payday,
                status: step.status,
                id: move.charge.id,
                date: move.charge.date,
                name: move.charge.name,
                amount: -move.charge.amount,
                missing: deeper,
                ...(move.charge.refusedBy
                  ? { refusedBy: move.charge.refusedBy }
                  : {}),
              },
            ]
          : [];
      });
    },
  );
}

function moved(
  inputs: WalkInputs,
  debtId: string | undefined,
  change: (charge: Occurrence) => Occurrence,
): WalkInputs {
  const ids = new Set(
    (inputs.debts ?? []).find(debt => debt.id === debtId)?.paymentScheduleIds ??
      [],
  );
  return {
    ...inputs,
    occurrences: inputs.occurrences.map(charge =>
      charge.scheduleId !== undefined && ids.has(charge.scheduleId)
        ? change(charge)
        : charge,
    ),
  };
}

/** The plan with one option taken. */
export function withOption(
  inputs: WalkInputs,
  option: OptimiseOption,
): WalkInputs {
  switch (option.kind) {
    case 'extra-hours': {
      const period = buildPeriods(inputs).find(p => p.payday === option.payday);
      if (!period) {
        return inputs;
      }
      const extra = option.hoursPerWeek ?? 0;
      return {
        ...inputs,
        pays: {
          ...inputs.pays,
          [period.payday]: {
            ...inputs.pays?.[period.payday],
            hours: [period.hours[0] + extra, period.hours[1] + extra],
          },
        },
      };
    }
    case 'reserve':
      // Drawn today: it reaches every charge from now on.
      return {
        ...inputs,
        incomes: [
          ...(inputs.incomes ?? []),
          {
            id: option.id,
            name: option.subject,
            amount: option.frees,
            date: inputs.today,
          },
        ],
      };
    case 'defer': {
      const days = option.days ?? 0;
      const end = addDays(inputs.today, days);
      return moved(inputs, option.ref, charge =>
        charge.date >= inputs.today && charge.date < end
          ? { ...charge, date: addDays(charge.date, days) }
          : charge,
      );
    }
    case 'partial':
      return {
        ...inputs,
        occurrences: withArrangements(inputs.occurrences, {
          [option.ref ?? '']: {
            now: option.now ?? 0,
            restOn: option.payday ?? inputs.today,
          },
        }),
      };
    default: {
      // Every payment of the debt brought down to the minimum, whatever its
      // rhythm: in the proportion of the minimum to what is planned.
      const debt = (inputs.debts ?? []).find(d => d.id === option.ref);
      const planned = debt?.plannedPayment ?? 0;
      if (!debt || planned <= debt.monthlyPayment) {
        return inputs;
      }
      return moved(inputs, debt.id, charge => ({
        ...charge,
        amount: Math.round((charge.amount * debt.monthlyPayment) / planned),
      }));
    }
  }
}

function withOptions(
  inputs: WalkInputs,
  options: readonly OptimiseOption[],
): WalkInputs {
  return options.reduce(withOption, inputs);
}

/**
 * The fewest extra hours on the pay being worked that pay what they can of
 * the shortfall: they only reach the charges due once that pay is received.
 */
function sizedHours(
  inputs: WalkInputs,
  option: OptimiseOption,
  base: number,
  maxHours: number,
): OptimiseOption | null {
  const open = buildPeriods(inputs).find(p => p.status === 'open');
  if (!open) {
    return null;
  }
  const at = (halves: number) =>
    base -
    leastShortfall(
      withOption(inputs, {
        ...option,
        payday: open.payday,
        hoursPerWeek: halves / 2,
      }),
    );
  let high = Math.round(maxHours * 2);
  const most = at(high);
  if (most < COVERED) {
    return null;
  }
  let low = 0;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (at(middle) >= most) {
      high = middle;
    } else {
      low = middle;
    }
  }
  return {
    ...option,
    payday: open.payday,
    hoursPerWeek: high / 2,
    frees: most,
  };
}

/**
 * Paying in part the bills whose creditor allows it (`scheduleIds`), in a
 * pay that misses money: on its day, what the pay can; the rest on the day
 * of the next pay. A bill put on a card, or already past, frees no cash.
 */
export function partialOptions(
  inputs: WalkInputs,
  scheduleIds: readonly string[],
): OptimiseOption[] {
  const allowed = new Set(scheduleIds);
  const short = shortfallByPay(inputs);
  return buildPeriods(inputs).flatMap(period => {
    const missing = short[period.payday] ?? 0;
    if (missing < COVERED) {
      return [];
    }
    return period.charges
      .filter(
        c =>
          !c.skipped &&
          c.cardId === undefined &&
          c.scheduleId !== undefined &&
          allowed.has(c.scheduleId) &&
          c.date >= inputs.today,
      )
      .map(c => {
        const total = -c.amount;
        const rest = Math.min(total, missing);
        return {
          id: `partial:${c.id}`,
          kind: 'partial' as const,
          ref: c.id,
          subject: c.name,
          frees: rest,
          cost: 0,
          now: total - rest,
          payday: period.nextPayday,
        };
      });
  });
}

/**
 * The options as the walk sees them: what each one pays of the charges,
 * each before its date. An option that comes too late for every charge left
 * short is left out.
 */
export function optionsInTime(
  inputs: WalkInputs,
  options: readonly OptimiseOption[],
  maxExtraHoursPerWeek = 20,
): OptimiseOption[] {
  const base = leastShortfall(inputs);
  return options.flatMap(option => {
    if (option.kind === 'extra-hours') {
      const sized = sizedHours(inputs, option, base, maxExtraHoursPerWeek);
      return sized ? [sized] : [];
    }
    const frees = base - leastShortfall(withOption(inputs, option));
    return frees >= COVERED ? [{ ...option, frees }] : [];
  });
}

/** True when, the options taken together, no charge is left unpaid. */
export function coversInTime(
  inputs: WalkInputs,
  options: readonly OptimiseOption[],
): boolean {
  return leastShortfall(withOptions(inputs, options)) < COVERED;
}

/**
 * The cheapest options, at most four, that leave no charge unpaid on its
 * date; none when no such set exists. On a tie, the fewest options.
 */
export function cheapestInTime(
  inputs: WalkInputs,
  options: readonly OptimiseOption[],
): OptimiseOption[] {
  const sets: OptimiseOption[][] = [];
  const grow = (set: OptimiseOption[], from: number) => {
    if (set.length > 0) {
      sets.push(set);
    }
    if (set.length === 4) {
      return;
    }
    options
      .slice(from)
      .forEach((option, i) => grow([...set, option], from + i + 1));
  };
  grow([], 0);
  const cost = (set: OptimiseOption[]) => sum(set.map(o => o.cost));
  return (
    sets
      .sort((a, b) => cost(a) - cost(b) || a.length - b.length)
      .find(set => coversInTime(inputs, set)) ?? []
  );
}

// ── Reference case ─────────────────────────────────────────────────────────

/**
 * A small plan worked out by hand, that the solver must find again on the
 * device it runs on. 40 h a week at 20 $ with no tax: 1 600 $ a pay. Charges
 * of 1 000 $, 1 000 $ then 2 000 $; a 100 $ cushion; a card at 1 000 $ of a
 * 1 000 $ limit, 26 % a year, so 1 % a fortnight.
 *
 * The third pay lacks 500 $ (2 000 + 100 − 1 600): the second keeps them.
 * That leaves 500 $ for the card at the first pay and 200 $ at the second.
 * Card: 1 000 + 10 − 500 = 510 ; 510 + 5,10 − 200 = 315,10 ; + 3,15 = 318,25.
 */
export function referenceCheck(solve: LpSolve): Check {
  const inputs: WalkInputs = {
    config: {
      hourlyRate: 2000,
      regularHoursPerWeek: 40,
      overtimeMultiplier: 1.5,
      minHoursPerWeek: 40,
      bonusPerPay: 0,
      fixedDeductions: 0,
      cappedContributions: 0,
      taxPoints: [],
      payday: '2026-01-01',
      payDelayDays: 0,
    },
    today: '2026-01-01',
    count: 3,
    cushion: 10000,
    occurrences: [
      { id: 'a', name: 'a', date: '2026-01-05', amount: -100000 },
      { id: 'b', name: 'b', date: '2026-01-20', amount: -100000 },
      { id: 'c', name: 'c', date: '2026-02-01', amount: -200000 },
    ],
    debts: [
      {
        id: 'card',
        name: 'Carte',
        balance: 100000,
        rate: 26,
        monthlyPayment: 0,
        canPayMore: true,
        creditLimit: 100000,
      },
    ],
  };
  const name = 'Le solveur retrouve le cas de référence calculé à la main';
  try {
    const steps = walkFortnights(inputs, solve);
    const found = JSON.stringify({
      extra: steps.map(s => s.debts[0].extra),
      balance: steps.map(s => s.debts[0].balanceAfter),
      cash: steps.map(s => s.cashOut),
      hours: steps.map(s => s.thresholdHoursPerWeek),
      short: steps.map(s => s.shortfall),
    });
    const expected = JSON.stringify({
      extra: [50000, 20000, 0],
      balance: [51000, 31510, 31825],
      cash: [10000, 50000, 10000],
      hours: [27.5, 35, 40],
      short: [0, 0, 0],
    });
    const failed = checkWalk(steps, inputs).find(check => !check.ok);
    return {
      name,
      ok: found === expected && !failed,
      detail:
        found !== expected
          ? `attendu ${expected}, trouvé ${found}`
          : failed
            ? `${failed.name} : ${failed.detail}`
            : '',
    };
  } catch (error) {
    return {
      name,
      ok: false,
      detail: error instanceof Error ? error.message : 'solveur en erreur',
    };
  }
}

// ── Self-checks ────────────────────────────────────────────────────────────

/**
 * Checks a walk against its inputs, recomputing each figure on its own. Shown
 * on the screen so that a wrong calculation is seen, not trusted.
 */
export function checkWalk(steps: WalkStep[], inputs: WalkInputs): Check[] {
  const cushion = inputs.cushion ?? 0;
  const dollars = (cents: number) => (cents / 100).toFixed(2);
  const checks: Check[] = [];
  const add = (name: string, problems: string[]) =>
    checks.push({ name, ok: problems.length === 0, detail: problems[0] ?? '' });
  const each = (test: (step: WalkStep, index: number) => string | null) =>
    steps.flatMap((step, index) => {
      const problem = test(step, index);
      return problem === null ? [] : [`Paie du ${step.payday} : ${problem}`];
    });

  const end = steps[steps.length - 1]?.nextPayday ?? '';
  const due = inputs.occurrences.filter(o => o.amount < 0 && o.date < end);
  const placed = steps.flatMap(s => s.charges.map(c => c.id));
  add('Chaque charge est dans une paie et une seule', [
    ...due
      .filter(o => placed.filter(id => id === o.id).length !== 1)
      .map(
        o =>
          `${o.name} du ${o.date} figure ${placed.filter(id => id === o.id).length} fois`,
      ),
    ...each((step, index) => {
      const stray = step.charges.find(
        c => c.date >= step.nextPayday || (index > 0 && c.date < step.payday),
      );
      return stray
        ? `${stray.name} du ${stray.date} est hors de la période`
        : null;
    }),
  ]);

  add(
    'Le net se recalcule depuis les heures',
    each(step => {
      if (!step.netIsEstimate) {
        return null;
      }
      const gross = grossPay(inputs.config, step.hours);
      const net = gross - withheldTax(inputs.config, gross) - step.deductions;
      return net === step.net && gross === step.gross
        ? null
        : `net affiché ${dollars(step.net)} $, recalculé ${dollars(net)} $`;
    }),
  );

  const spent = (step: WalkStep) =>
    sum(step.debts.map(d => d.extra)) + sum(step.needs.map(n => n.paid));
  add(
    'Ce qui entre = ce qui sort, au centime',
    each(step => {
      const charges = sum(step.charges.filter(isCash).map(c => -c.amount));
      const incomes = sum(step.incomes.map(i => i.amount));
      const into = step.cashIn + step.net + incomes + step.shortfall;
      const out = charges + spent(step) + step.refund + step.cashOut;
      return into === out
        ? null
        : `${dollars(into)} $ entrés, ${dollars(out)} $ sortis`;
    }),
  );

  add(
    'Le compte ne passe jamais sous le coussin',
    each(step =>
      step.cashOut >= cushion
        ? null
        : `solde de ${dollars(step.cashOut)} $ sous le coussin de ${dollars(cushion)} $`,
    ),
  );

  add(
    "Le minimum d'heures est juste, à un dixième près",
    each(step => {
      const hours = step.thresholdHoursPerWeek;
      if (step.requiredNet <= 0) {
        return hours === 0 ? null : `${hours} h pour un besoin nul`;
      }
      // Beyond 100 h a week no number of hours is offered.
      const enough =
        hours >= 100 ||
        netPay(inputs.config, [hours, hours]) >= step.requiredNet;
      const less = Math.round((hours - 0.1) * 10) / 10;
      const tight = netPay(inputs.config, [less, less]) < step.requiredNet;
      return enough && tight
        ? null
        : `${hours} h ne correspond pas au net requis de ${dollars(step.requiredNet)} $`;
    }),
  );

  add(
    'Toute mise de côté donnée est reçue',
    each((step, index) => {
      const expected =
        index === 0 ? (inputs.available ?? 0) : steps[index - 1].cashOut;
      return step.cashIn === expected
        ? null
        : `reçoit ${dollars(step.cashIn)} $ au lieu de ${dollars(expected)} $`;
    }),
  );

  add(
    'Aucune dette servie avant une charge',
    each(step => {
      // What was already missing before the pay arrived is not its doing.
      let running = step.cashIn;
      let before = 0;
      for (const flow of flows(step, 0)) {
        running += flow.amount;
        if (flow.date < step.payday && flow.amount < 0) {
          before = Math.max(before, -running);
        }
      }
      return step.shortfall > before && spent(step) > 0
        ? `${dollars(spent(step))} $ versés alors qu'il manque ${dollars(step.shortfall)} $`
        : null;
    }),
  );

  add(
    "Cartes au-dessus de 30 % servies d'abord",
    each(step => {
      const waiting = step.debts.find(
        d =>
          d.limit !== null &&
          d.balanceAfter > TARGET_USE * d.limit + 100 &&
          inputs.debts?.find(debt => debt.id === d.id)?.canPayMore !== false,
      );
      // Between two cards the plan may well pay one below 30 % ahead of
      // purchases to come; what it may never do is send money to a debt
      // without a limit while a card still stands above 30 %.
      const served = step.debts.find(d => d.extra > 100 && d.limit === null);
      return waiting && served
        ? `${served.name} reçoit ${dollars(served.extra)} $ alors que ${waiting.name} reste au-dessus de 30 %`
        : null;
    }),
  );

  add(
    "Aucun versement au-delà du solde d'une dette",
    each((step, index) => {
      for (const debt of step.debts) {
        const start =
          index === 0
            ? Math.max(
                0,
                inputs.debts?.find(d => d.id === debt.id)?.balance ?? 0,
              )
            : (steps[index - 1].debts.find(d => d.id === debt.id)
                ?.balanceAfter ?? 0);
        const after =
          start + debt.interest - debt.scheduled + debt.purchases - debt.extra;
        if (after !== debt.balanceAfter || after < 0) {
          return `${debt.name} : solde affiché ${dollars(debt.balanceAfter)} $, recalculé ${dollars(after)} $`;
        }
      }
      return null;
    }),
  );

  const counted = steps.flatMap(s => s.incomes.map(i => i.id));
  add(
    "Chaque entrée d'argent comptée une seule fois",
    (inputs.incomes ?? [])
      .filter(i => i.date < end)
      .filter(i => counted.filter(id => id === i.id).length !== 1)
      .map(
        i =>
          `${i.name} figure ${counted.filter(id => id === i.id).length} fois`,
      ),
  );

  add(
    'Jour par jour, le compte reste positif',
    each(step => {
      let running = step.cashIn + step.shortfall;
      for (const flow of flows(step, spent(step))) {
        running += flow.amount;
        if (running < 0) {
          return `solde de ${dollars(running)} $ le ${flow.date}`;
        }
      }
      return running === step.cashOut
        ? null
        : `solde final ${dollars(running)} $, affiché ${dollars(step.cashOut)} $`;
    }),
  );

  add(
    "Un paiement désactivé n'est jamais payé",
    each(step => {
      const off = step.charges.filter(c => c.skipped);
      const paid = sum(step.charges.filter(isCash).map(c => -c.amount));
      if (paid !== step.totalCharges) {
        return `total des charges ${dollars(step.totalCharges)} $, recalculé ${dollars(paid)} $`;
      }
      const reduced = off.find(c => {
        const debt = step.debts.find(d => d.id === c.debtId);
        const others = sum(
          step.charges
            .filter(o => !o.skipped && o.debtId === c.debtId)
            .map(o => -o.amount),
        );
        return debt !== undefined && debt.scheduled > others;
      });
      return reduced
        ? `${reduced.name} a réduit une dette alors qu'il est désactivé`
        : null;
    }),
  );

  const replayed = replay(inputs, {
    debt: Object.fromEntries(
      (inputs.debts ?? []).map(d => [
        d.id,
        steps.map(s => s.debts.find(x => x.id === d.id)?.extra ?? 0),
      ]),
    ),
    need: Object.fromEntries(
      (steps[0]?.needs ?? []).map(n => [
        n.id,
        steps.map(s => s.needs.find(x => x.id === n.id)?.paid ?? 0),
      ]),
    ),
  });
  add(
    'La répartition, refaite à la main, tient',
    each((step, index) => {
      const again = replayed[index];
      return again.cashOut === step.cashOut &&
        again.shortfall === step.shortfall
        ? null
        : `solde ${dollars(step.cashOut)} $, refait ${dollars(again.cashOut)} $`;
    }),
  );

  const best = score(steps);
  const plain = score(replay(inputs, cascade(inputs)));
  let verdict: string[] = [];
  for (const level of LEVELS) {
    // A dollar of room per level: the plan is rounded down to the cent.
    const room = Math.max(100, plain[level] * 1e-4);
    if (best[level] > plain[level] + room) {
      verdict = [
        `${level} : ${dollars(best[level])} contre ${dollars(plain[level])} pour la répartition simple`,
      ];
      break;
    }
    // Any real gain on a level outranks whatever the levels below cost.
    if (best[level] < plain[level] - 1) {
      break;
    }
  }
  add(
    "L'optimisation fait au moins aussi bien que la répartition simple",
    verdict,
  );

  return checks;
}
